import React from "react";
import * as ReactDOMClient from "react-dom/client";
import { ReactController } from "./react_controller";
import {
  TipTapHandlebarsEditor,
  HandlebarsVariable,
  GregPrimerTheme,
} from "@getregistered/greg-editors";

type EditorContext = "email" | "event-site" | "pdf";

interface TipTapHandlebarsControllerElement extends HTMLElement {
  dataset: {
    controller: string;
    tipTapHandlebarsVariablesUrlValue?: string;
    tipTapHandlebarsFileUploadUrlValue?: string;
    tipTapHandlebarsContextValue?: string;
  };
}

export class TipTapHandlebarsController extends ReactController<TipTapHandlebarsControllerElement> {
  static values = {
    variablesUrl: String,
    fileUploadUrl: String,
    context: String,
  };

  declare readonly variablesUrlValue: string;
  declare readonly fileUploadUrlValue: string;
  declare readonly contextValue: string;
  declare readonly element: TipTapHandlebarsControllerElement;
  private textArea!: HTMLTextAreaElement;
  private content: string = "";
  private availableVariables: HandlebarsVariable[] = [];
  private reactRootEl?: HTMLDivElement;
  declare _root?: ReactDOMClient.Root;

  connect() {
    this.textArea = this.textAreaElement;
    this.textArea.style.display = "none";
    this.content = this.textArea.value;
    this.textArea.addEventListener("change", this.handleTextAreaChange);

    // Create wrapper div for React root (inserted after textarea)
    this.reactRootEl = document.createElement("div");
    const rootEl = (this.element as HTMLElement).nodeName === "TEXTAREA" 
      ? (this.element as HTMLElement).parentElement! 
      : (this.element as HTMLElement);
    rootEl.insertBefore(this.reactRootEl, this.textArea.nextSibling);

    if (this.variablesUrlValue) {
      this.fetchVariables();
    } else {
      this.render();
    }
  }

  private handleTextAreaChange = (e: Event) => {
    const newContent = (e.target as HTMLTextAreaElement).value;
    if (newContent !== this.content) {
      this.handleContentChange(newContent);
    }
  };

  async fetchVariables() {
    try {
      const response = await fetch(this.variablesUrlValue);
      const data = await response.json();
      this.availableVariables = data.variables || [];
      this.render();
    } catch (error) {
      console.error("Failed to fetch handlebars variables:", error);
      this.render();
    }
  }

  private handleContentChange = (content: string) => {
    this.content = content;
    this.textArea.value = content;
    // Dispatch events so forms can track dirty state
    this.textArea.dispatchEvent(new Event("input", { bubbles: true }));
    this.textArea.dispatchEvent(new Event("change", { bubbles: true }));
    this.render();
  };

  disconnect() {
    if (this.textArea) {
      this.textArea.removeEventListener("change", this.handleTextAreaChange);
    }
    if (this._root) {
      this._root.unmount();
    }
    super.disconnect();
  }

  // Override root getter to use wrapper div (inserted after textarea)
  get root(): ReactDOMClient.Root {
    if (!this.reactRootEl) {
      throw new Error("React root element not initialized");
    }
    if (this._root === undefined) {
      this._root = ReactDOMClient.createRoot(this.reactRootEl);
    }
    return this._root;
  }

  render() {
    const context = (this.contextValue || "event-site") as EditorContext;
    this.root.render(
      <GregPrimerTheme>
        <TipTapHandlebarsEditor
          content={this.content}
          onChange={this.handleContentChange}
          availableVariables={this.availableVariables}
          fileUploadUrl={this.fileUploadUrlValue}
          context={context}
        />
      </GregPrimerTheme>
    );
  }

  get textAreaElement(): HTMLTextAreaElement {
    if (this.element.nodeName === "TEXTAREA") {
      return this.element as unknown as HTMLTextAreaElement;
    } else {
      const textarea = this.element.querySelector("textarea");
      if (!textarea) {
        throw new Error("No textarea found in controller element");
      }
      return textarea;
    }
  }

}

